import { ChangeDetectorRef, Component, Input, OnChanges, SimpleChanges } from "@angular/core";
import { BaseChartComponent } from "./base-chart.component";
import { ChartConfiguration, Plugin } from "chart.js/auto";
import {
  CHART_LEVEL1_ZONE_COLOR,
  CHART_LEVEL2_ZONE_COLOR,
  CHART_LEVEL3_ZONE_COLOR,
  CHART_LEVEL4_ZONE_COLOR,
  PRIMARY_LINE_COLOR,
} from "src/app/constants/index";
import { TranslateService } from "@ngx-translate/core";

@Component({
  selector: "app-thermal-sensation-chart",
  templateUrl: "./chart.component.html",
  styleUrls: ["./chart.component.scss"],
})
export class ThermalSensationChartComponent extends BaseChartComponent implements OnChanges {
  private readonly TEMP_MIN_THRESHOLDS = [-10, -28, -54];
  private readonly TEMP_MAX_THRESHOLDS = [27, 33, 54];

  private calculatedYMin: number = -10;
  private calculatedYMax: number = 50;

  constructor(translate: TranslateService, cdr: ChangeDetectorRef) {
    super(translate, cdr);
  }

  public ngOnChanges(changes: SimpleChanges): void {
    // Update the data property for app-empty-data component
    if (this.data?.length) {
      // Force chart re-rendering when data changes
      if (changes.data && this.chartContainer) {
        setTimeout(() => {
          this.renderChart();
          this.cdr.detectChanges();
        }, 0);
        return;
      }
    }

    // Call parent's ngOnChanges to ensure base component logic runs
    super.ngOnChanges(changes);
  }

  private drawThermalSensationAlertLevelsPlugin: Plugin = {
    id: "temperatureAlerts",
    beforeDraw: (): void => {
      if (this.chart) {
        const {
          ctx,
          chartArea: { left, top, right, bottom },
          scales: { y },
        } = this.chart;

        const yMin = this.calculatedYMin;
        const yMax = this.calculatedYMax;

        // Solo proceder si algún umbral está dentro del rango visible
        const shouldDrawMaxAlerts = this.TEMP_MAX_THRESHOLDS.some(threshold => threshold >= yMin && threshold <= yMax);
        const shouldDrawMinAlerts = this.TEMP_MIN_THRESHOLDS.some(threshold => threshold >= yMin && threshold <= yMax);
        const shouldDrawNormalZone = yMin <= this.TEMP_MAX_THRESHOLDS[0] && yMax >= this.TEMP_MIN_THRESHOLDS[0];

        if (!shouldDrawMaxAlerts && !shouldDrawMinAlerts && !shouldDrawNormalZone) {
          return;
        }

        // Obtener posiciones en píxeles para los umbrales máximos
        const maxLevel1 = y.getPixelForValue(this.TEMP_MAX_THRESHOLDS[0]); // Nivel 1
        const maxLevel2 = y.getPixelForValue(this.TEMP_MAX_THRESHOLDS[1]); // Nivel 2
        const maxLevel3 = y.getPixelForValue(this.TEMP_MAX_THRESHOLDS[2]); // Nivel 3
        const maxLevel4 = y.getPixelForValue(this.TEMP_MAX_THRESHOLDS[3]); // Nivel 4

        // Obtener posiciones en píxeles para los umbrales mínimos
        const minLevel1 = y.getPixelForValue(this.TEMP_MIN_THRESHOLDS[0]); // Nivel 1
        const minLevel2 = y.getPixelForValue(this.TEMP_MIN_THRESHOLDS[1]); // Nivel 2
        const minLevel3 = y.getPixelForValue(this.TEMP_MIN_THRESHOLDS[2]); // Nivel 3
        const minLevel4 = y.getPixelForValue(this.TEMP_MIN_THRESHOLDS[3]); // Nivel 4

        ctx.save();

        // Zona normal (entre umbrales mínimos y máximos) [nivel1]
        if (shouldDrawNormalZone) {
          const normalZoneTop = Math.max(maxLevel1, top);
          const normalZoneBottom = Math.min(minLevel1, bottom);
          if (normalZoneBottom > normalZoneTop) {
            ctx.fillStyle = CHART_LEVEL1_ZONE_COLOR;
            ctx.fillRect(left, normalZoneTop, right - left, normalZoneBottom - normalZoneTop);
          }
        }

        // Zonas de temperatura máxima (solo si están en el rango visible)
        if (shouldDrawMaxAlerts) {
          // Zona de alerta nivel 2 calor (nivel2 - nivel3)
          if (yMax > this.TEMP_MAX_THRESHOLDS[0] && yMin < this.TEMP_MAX_THRESHOLDS[1]) {
            const level2Top = Math.max(maxLevel2, top);
            const level1Bottom = Math.min(maxLevel1, bottom);
            if (level1Bottom > level2Top && level2Top < bottom && level1Bottom > top) {
              ctx.fillStyle = CHART_LEVEL2_ZONE_COLOR;
              ctx.fillRect(left, level2Top, right - left, level1Bottom - level2Top);
            }
          }

          // Zona de alerta nivel 3 calor (nivel3 - nivel4)
          if (yMax > this.TEMP_MAX_THRESHOLDS[1] && yMin < this.TEMP_MAX_THRESHOLDS[2]) {
            const level3Top = Math.max(maxLevel3, top);
            const level2Bottom = Math.min(maxLevel2, bottom);
            if (level2Bottom > level3Top && level3Top < bottom && level2Bottom > top) {
              ctx.fillStyle = CHART_LEVEL3_ZONE_COLOR;
              ctx.fillRect(left, level3Top, right - left, level2Bottom - level3Top);
            }
          }

          // Zona de alerta nivel 4 calor (> nivel4)
          if (yMax > this.TEMP_MAX_THRESHOLDS[2]) {
            const zoneTop = Math.max(top, top);
            const zoneBottom = Math.min(maxLevel3, bottom);
            if (zoneBottom > zoneTop && zoneBottom > top) {
              ctx.fillStyle = CHART_LEVEL4_ZONE_COLOR;
              ctx.fillRect(left, zoneTop, right - left, zoneBottom - zoneTop);
            }
          }
        }

        // Zonas de temperatura mínima (solo si están en el rango visible)
        if (shouldDrawMinAlerts) {
          // Zona de alerta nivel 2 frío (nivel2 a nivel3)
          if (yMin < this.TEMP_MIN_THRESHOLDS[0] && yMax > this.TEMP_MIN_THRESHOLDS[1]) {
            const level1Top = Math.max(minLevel1, top);
            const level2Bottom = Math.min(minLevel2, bottom);
            if (level2Bottom > level1Top && level1Top < bottom && level2Bottom > top) {
              ctx.fillStyle = CHART_LEVEL2_ZONE_COLOR;
              ctx.fillRect(left, level1Top, right - left, level2Bottom - level1Top);
            }
          }

          // Zona de alerta nivel 3 frío (nivel3 a nivel4)
          if (yMin < this.TEMP_MIN_THRESHOLDS[1] && yMax > this.TEMP_MIN_THRESHOLDS[2]) {
            const level2Top = Math.max(minLevel2, top);
            const level3Bottom = Math.min(minLevel3, bottom);
            if (level3Bottom > level2Top && level2Top < bottom && level3Bottom > top) {
              ctx.fillStyle = CHART_LEVEL3_ZONE_COLOR;
              ctx.fillRect(left, level2Top, right - left, level3Bottom - level2Top);
            }
          }

          // Zona de alerta nivel 4 frío (< nivel4)
          if (yMin < this.TEMP_MIN_THRESHOLDS[2]) {
            const zoneTop = Math.max(minLevel3, top);
            const zoneBottom = Math.min(bottom, bottom);
            if (zoneBottom > zoneTop && zoneTop < bottom) {
              ctx.fillStyle = CHART_LEVEL4_ZONE_COLOR;
              ctx.fillRect(left, zoneTop, right - left, zoneBottom - zoneTop);
            }
          }
        }

        ctx.restore();
      }
    },
  };

  /**
   * Calcula los límites del eje Y según las nuevas reglas de márgenes
   */
  private calculateYAxisLimits(minTemp: number, maxTemp: number): { min: number; max: number } {
    let ySuggestedMin = Math.floor(minTemp / 5) * 5;
    let ySuggestedMax = Math.ceil(maxTemp / 5) * 5;

    const maxDistance = Math.ceil(maxTemp) - ySuggestedMax;
    const minDistance = ySuggestedMin - Math.floor(minTemp);
    // Si los límites están cerca del máximo (a 1 unidad) sumamos 5 al maximo
    if (maxDistance >= -1) {
      ySuggestedMax += 5;
    }
    if (minDistance >= -1) {
      ySuggestedMin -= 5;
    }

    // Ensure there's a visible range if all data points are the same multiple of 5
    if (ySuggestedMin === ySuggestedMax) {
      ySuggestedMin -= 5;
      ySuggestedMax += 5;
    }

    return {
      min: ySuggestedMin,
      max: ySuggestedMax,
    };
  }

  protected getChartConfig(): ChartConfiguration {
    const transformedData = this.data.map(point => {
      const timestamp = new Date(point.timestamp).getTime();
      if (!(point.value || point.value === 0)) {
        return { x: timestamp, y: null };
      }
      return { x: timestamp, y: point.value };
    });

    const xSuggestedMax = !this.currentTime ? new Date().getTime() : undefined;

    // Calculate dynamic y-axis limits based on transformed data
    const validYValues = transformedData.map(point => point.y).filter(value => value !== null);

    let ySuggestedMin: number;
    let ySuggestedMax: number;

    if (validYValues.length > 0) {
      const minTemp = Math.min(...validYValues);
      const maxTemp = Math.max(...validYValues);

      // Aplicar las nuevas reglas de márgenes
      const limits = this.calculateYAxisLimits(minTemp, maxTemp);
      ySuggestedMin = limits.min;
      ySuggestedMax = limits.max;
    } else {
      // Valores por defecto cuando no hay datos
      ySuggestedMin = -10;
      ySuggestedMax = 50;
    }

    this.calculatedYMin = ySuggestedMin;
    this.calculatedYMax = ySuggestedMax;

    // Calculate X-axis limits based on all timestamps (include points with y === null)
    const allXValues = transformedData.map(point => point.x as number);
    const xMin = allXValues.length > 0 ? Math.min(...allXValues) : undefined;
    const xMax = allXValues.length > 0 ? Math.max(...allXValues) : xSuggestedMax;

    // Get common options
    const commonOptions = this.getCommonChartOptions(xSuggestedMax, ySuggestedMax, "°C", ySuggestedMin);

    return {
      type: "line",
      data: {
        datasets: [
          {
            data: transformedData,
            borderColor: PRIMARY_LINE_COLOR,
            backgroundColor: PRIMARY_LINE_COLOR,
            borderWidth: 2,
            pointRadius: 1,
            tension: 0.5,
            parsing: false,
          },
        ],
      },
      options: {
        ...commonOptions,
        scales: {
          ...commonOptions.scales,
          x: {
            ...commonOptions.scales?.x,
            // Force axis to include first/last timestamps even if their y is null
            min: xMin,
            max: xMax,
          },
          y: {
            type: "linear",
            min: ySuggestedMin,
            max: ySuggestedMax,
            grid: commonOptions.scales?.y?.grid,
            ticks: {
              ...commonOptions.scales?.y?.ticks,
              stepSize: 5,
            },
          },
        },
      },
      plugins: [this.drawThermalSensationAlertLevelsPlugin],
    };
  }
}
